import { memo, useCallback, useEffect, useMemo, useSyncExternalStore, useState } from 'react';
import { Popover, Transition } from '@headlessui/react';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { getTextElementById } from '../utils';
import { updateTextElementShadow, removeTextElementShadow, getTextElementShadow } from './textElementPopoverUtils';

import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { brandKit } from '@/stores/brandKit';
import { classnames } from '@/libs/utils';
import Toggle from '@/components/atoms/Toggle';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import Icon from '@/components/atoms/Icon';

interface TextElementShadowPopoverProps {
  textElementId: string;
}

function TextElementShadowPopover({ textElementId }: TextElementShadowPopoverProps) {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const usePlatformBrandKit = !!featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [lastShadowState, setLastShadowState] = useState<{ blur: number; color: string } | null>(null);

  useEffect(() => {
    setLastShadowState(null);
  }, [textElementId]);

  const selectedElement = useMemo(() => {
    return getTextElementById(clipData, textElementId);
  }, [clipData, textElementId]);

  const shadowToggleEnabled = useMemo(() => {
    return selectedElement ? !!selectedElement.style?.textShadow : false;
  }, [selectedElement]);

  const { blur, color } = useMemo(() => {
    return getTextElementShadow(selectedElement);
  }, [selectedElement]);

  const onToggleShadow = useCallback(() => {
    if (shadowToggleEnabled) {
      setLastShadowState({ blur, color });
      removeTextElementShadow(clipId, clipData, textElementId);
    } else {
      const restoredBlur = lastShadowState?.blur ?? (blur > 0 ? blur : 10);
      const restoredColor = lastShadowState?.color ?? color;
      updateTextElementShadow(clipId, clipData, textElementId, restoredBlur, restoredColor);
    }
  }, [shadowToggleEnabled, clipId, clipData, textElementId, blur, color, lastShadowState]);

  const updateShadow = useCallback(
    (newValues: { blur?: number; color?: string }) => {
      const updatedBlur = newValues.blur ?? blur;
      const updatedColor = newValues.color ?? color;
      updateTextElementShadow(clipId, clipData, textElementId, updatedBlur, updatedColor);
    },
    [blur, color, clipId, clipData, textElementId]
  );

  const onBlurChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateShadow({ blur: Number(e.target.value) });
    },
    [updateShadow]
  );

  const onShadowColorChange = useCallback(
    (value?: string) => {
      if (value) {
        updateShadow({ color: value });
      }
    },
    [updateShadow]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="text-element-shadow-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <Icon name="IconShadow" size={16} />
              <PlayerMenuTooltip content="Shadow" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            afterLeave={() => setIsColorPickerOpen(false)}
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="rounded-lg border bg-white shadow-lg">
                {usePlatformBrandKit && isColorPickerOpen ? (
                  <ColorPicker
                    defaultColorsLabel="Shadow Color"
                    onChange={onShadowColorChange}
                    hex={color}
                    inline
                    onBack={() => setIsColorPickerOpen(false)}
                  />
                ) : (
                  <div className="space-y-4 p-4">
                    {/* Enable/Disable Shadow */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Shadow</span>
                      <Toggle on={shadowToggleEnabled} onToggle={onToggleShadow} />
                    </div>

                    {shadowToggleEnabled && (
                      <>
                        {/* Blur */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-4 text-sm font-medium">Blur</span>
                          <span className="col-span-8 flex items-center gap-2">
                            <input
                              type="range"
                              min={0}
                              max={30}
                              value={blur}
                              step={1}
                              onChange={onBlurChange}
                              className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                            />
                            <span className="ml-1 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                              {blur}
                            </span>
                          </span>
                        </div>

                        {/* Shadow Color */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-4 text-sm font-medium">Color</span>
                          <ColorPicker
                            defaultColors={brandKitStore.colors}
                            defaultColorsLabel="Shadow Color"
                            onChange={onShadowColorChange}
                            hex={color}
                            onOpen={usePlatformBrandKit ? () => setIsColorPickerOpen(true) : undefined}
                            children={
                              <div className="flex size-full items-center justify-between bg-white p-2">
                                <div
                                  className="size-6 rounded border"
                                  style={{
                                    background: color
                                  }}
                                />
                                <span className="font-mono text-xs text-slate-400">{color}</span>
                              </div>
                            }
                            classNames="relative col-span-8 flex w-full items-center justify-between rounded-md border overflow-hidden"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(TextElementShadowPopover);
